import React, { forwardRef, useRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import FieldContainer from './components/FieldContainer'
import InputContainer from './components/InputContainer'
import styles from './Dropdown.module.scss'
import cls from 'classnames'
import { badgeProps, fieldContainerProps, fieldProps } from './commonProps'
import useInternalState from './components/useInternalState'
import withAnalytics from './components/withAnalytics'
import useAutoFocus from './components/useAutoFocus'

/** Field for dropdown menu inputs */
const Dropdown = forwardRef(({
  id,
  error,
  warning,
  touched,
  focus,
  placeholder,
  name,
  label,
  initialValue,
  className,
  inputRef,
  inputProps,
  disabled,
  sensitive,
  leftBadge,
  rightBadge,
  hideCheckIcon,
  children,
  autoFocus,
  autoFocusDelay,
  appearance
}, ref) => {
  const { internalFocus, internalTouched, internalInputProps } = useInternalState({ focus, touched, inputProps }),
    hasError = internalTouched && !!error,
    managedInputRef = useRef(null),
    filled = inputProps.value !== undefined ? !!inputProps.value : !!((managedInputRef.current && managedInputRef.current.value) || inputProps.value)

  useImperativeHandle(inputRef, () => managedInputRef.current)

  useAutoFocus(managedInputRef, autoFocus, autoFocusDelay)

  return (
    <FieldContainer ref={ref} id={id} error={error} warning={warning} touched={internalTouched} focus={internalFocus} className={className} appearance={appearance}>
      <InputContainer label={label} hasError={hasError} filled={filled} disabled={disabled} focus={internalFocus} sensitive={sensitive} leftBadge={leftBadge} rightBadge={rightBadge} hideCheckIcon={hideCheckIcon} appearance={appearance}>
        {({inputStyles, hasIcon}) =>
          <>
            <select
              data-testid="inputElement"
              {...internalInputProps}
              className={inputStyles}
              name={name}
              id={id}
              ref={managedInputRef}
              disabled={disabled}
              defaultValue={inputProps.value === undefined ? initialValue : undefined}
              tabIndex={0}
            >
              {placeholder && <option value="" disabled>{placeholder}</option>}
              {children}
            </select>
            {!disabled && <i className={cls('icon-chevron-down medium', styles.arrow, {[styles.arrowWithIcon]: hasIcon, [styles.arrowWithBadge]: !!rightBadge, [styles.defaultStyle]: appearance === 'default', [styles.compactStyle]: appearance === 'compact'})}/>}
          </>
        }
       </InputContainer>
    </FieldContainer>
  )
})

Dropdown.propTypes = {
  ...fieldContainerProps,
  ...fieldProps,
  ...badgeProps,
  /** Text that will be shown inside the input before the user enters any data */
  placeholder: PropTypes.string,
  /** The label displayed to the user after the field has any value */
  label: PropTypes.string,
  /** The initial value to be displayed in the input */
  initialValue: PropTypes.string,
  /** Adds a class to instruct inspectlet and other libraries that the field should not have it's value disclosed on analytics */
  sensitive: PropTypes.bool,
  /** True if hide the check icon for filled field */
  hideCheckIcon: PropTypes.bool
}

Dropdown.defaultProps = {
  error: null,
  warning: null,
  touched: null,
  placeholder: null,
  label: null,
  initialValue: '',
  focus: null,
  inputRef: null,
  inputProps: {},
  disabled: false,
  sensitive: false,
  leftBadge: null,
  rightBadge: null,
  hideCheckIcon: false,
  autoFocus: false,
  autoFocusDelay: 500,
  appearance: 'default'
}

export default withAnalytics(Dropdown, {disabled: false, events: ['onChange']})
